<template>
  <div class="emotional-state-container">
    <div class="page-header">
      <h2>Чек-лист психоэмоционального состояния</h2>
    </div>

    <div style="display: flex; align-items: center">
      <div class="class-selector" v-if="userRole === 'admin'">
        <label>Класс:</label>
        <select
          class="form-control"
          v-model="selectedClassSelect"
          @change="loadStudents"
          id="selected_class"
        >
          <option value="">Все классы</option>
          <option v-for="cls in classList" :key="cls._id" :value="cls">
            {{ cls.className }}
          </option>
        </select>
      </div>

      <div
        class="risk-filter"
        v-if="studentsList.length > 0 && selectedClassSelect != ''"
      >
        <label>Фильтр по риску: от {{ riskFilter }}%</label>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          v-model="riskFilter"
          class="risk-slider"
        />
        <button
          v-if="selectedClassSelect != ''"
          @click="riskFilter = 0"
          class="clear-filter-btn"
          style="max-width: 70px; max-height: 30px; margin: 5px"
        >
          Сбросить
        </button>
      </div>
      <div
        class="risk-filter"
        v-if="studentsList.length > 0 && selectedClassSelect == ''"
      >
        <label>Фильтр по риску: от {{ riskFilter }}%</label>
        <input
          type="range"
          min="1"
          max="100"
          step="1"
          v-model="riskFilter"
          class="risk-slider"
        />
        <button
          v-if="selectedClassSelect == ''"
          @click="riskFilter = 1"
          class="clear-filter-btn"
          style="max-width: 70px; max-height: 30px; margin: 5px"
        >
          Сбросить
        </button>
      </div>

      <div class="print-controls" v-if="filteredStudentsList.length > 0">
        <button @click="printTable" class="print-btn">🖨️ Печать таблицы</button>
      </div>
      <div style="margin: 10px">
        Всего учеников: <br />
        {{ filteredStudentsList.length }}
      </div>
    </div>

    <div class="students-table" v-if="filteredStudentsList.length > 0">
      <table>
        <thead>
          <tr class="main-headers">
            <th rowspan="2" class="student-col">Ученик</th>
            <th colspan="3" class="section-header">Эмоциональное состояние</th>
            <th colspan="3" class="section-header">
              Социальное взаимодействие
            </th>
            <th colspan="3" class="section-header">Поведение</th>
            <th colspan="3" class="section-header">Мысли и чувства</th>
            <th colspan="4" class="section-header">Внешние обстоятельства</th>
            <th
              rowspan="2"
              class="score-col"
              style="cursor: help"
              title="Будет изменяться при обновлении любого поля: изменение любого поля с галочкой, комментарий. Все это обновит Дату обновления."
            >
              Дата обновления
            </th>
            <th rowspan="2" class="score-col">
              Комментарий <br />
              (для обновления нажмите на процент риска)
            </th>
            <th
              rowspan="2"
              class="score-col"
              style="cursor: help"
              title="Расчет риска (0-100%): учитываются отмеченные показатели с весами: 'Мысли и чувства' (×3), 'Поведение' (×2), 'Внешние обстоятельства' (×1.5), 'Эмоциональное состояние' (×1), 'Социальное взаимодействие' (×1). Итог = (сумма весов отмеченных пунктов / сумма весов всех пунктов) × 100%. Пример: риск 60% означает, что отмечено 60% от максимально возможного взвешенного значения."
            >
              Риск
            </th>
          </tr>
          <tr class="vertical-headers">
            <!-- Эмоциональное состояние -->
            <th
              class="vertical-text"
              title="Часто плохое настроение"
              @mouseenter="highlightColumn(1)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">Часто плохое настроение</div>
            </th>
            <th
              class="vertical-text"
              title="Часто тревожное, подавленное настроение"
              @mouseenter="highlightColumn(2)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Часто тревожное, подавленное настроение
              </div>
            </th>
            <th
              class="vertical-text"
              title="Раздражителен, агрессивен"
              @mouseenter="highlightColumn(3)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">Раздражителен, агрессивен</div>
            </th>
            <!-- Социальное взаимодействие -->
            <th
              class="vertical-text"
              title="Замкнут, стремится к уединению"
              @mouseenter="highlightColumn(4)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">Замкнут, стремится к уединению</div>
            </th>
            <th
              class="vertical-text"
              title="Ссоры с одноклассниками и педагогами"
              @mouseenter="highlightColumn(5)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Ссоры с одноклассниками и педагогами
              </div>
            </th>
            <th
              class="vertical-text"
              title="Излишне самоуверен/чувство неполноценности"
              @mouseenter="highlightColumn(6)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Излишне самоуверен/чувство неполноценности
              </div>
            </th>
            <!-- Поведение -->
            <th
              class="vertical-text"
              title="Отказ от привычных видов деятельности"
              @mouseenter="highlightColumn(7)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Отказ от привычных видов деятельности
              </div>
            </th>
            <th
              class="vertical-text"
              title="Самоповреждения (порезы, царапины и т.д.)"
              @mouseenter="highlightColumn(8)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Самоповреждения (порезы, царапины и т.д.)
              </div>
            </th>
            <th
              class="vertical-text"
              title="Неопрятный внешний вид"
              @mouseenter="highlightColumn(9)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">Неопрятный внешний вид</div>
            </th>
            <!-- Мысли и чувства -->
            <th
              class="vertical-text"
              title="Негативные высказывания о себе и своей жизни"
              @mouseenter="highlightColumn(10)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Негативные высказывания о себе и своей жизни
              </div>
            </th>
            <th
              class="vertical-text"
              title="Жалобы на физические боли без медицинских показаний"
              @mouseenter="highlightColumn(11)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Жалобы на физические боли без медицинских показаний
              </div>
            </th>
            <th
              class="vertical-text"
              title="Разговоры о смерти"
              @mouseenter="highlightColumn(12)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">Разговоры о смерти</div>
            </th>
            <!-- Внешние обстоятельства -->
            <th
              class="vertical-text"
              title="Отвержение коллективом одноклассников"
              @mouseenter="highlightColumn(13)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Отвержение коллективом одноклассников
              </div>
            </th>
            <th
              class="vertical-text"
              title="Является объектом травли со стороны детей"
              @mouseenter="highlightColumn(14)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">
                Является объектом травли со стороны детей
              </div>
            </th>
            <th
              class="vertical-text"
              title="Неустойчивая семейная ситуация (конфликты, развод, низкий экономический статус)"
              @mouseenter="highlightColumn(15)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">Неустойчивая семейная ситуация</div>
            </th>
            <th
              class="vertical-text"
              title="Значимая потеря (человека, отношений, предмета, увлечения)"
              @mouseenter="highlightColumn(16)"
              @mouseleave="resetHighlight"
            >
              <div class="vertical-content">Значимая потеря</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="student in filteredStudentsList"
            :key="student._id"
            class="student-row"
          >
            <td class="student-info">
              <strong
                >{{ getStudentClass(student) }} {{ student.FirstName }}
                {{ student.LastName }}
              </strong>
            </td>

            <!-- Эмоциональное состояние -->
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 1 }"
              @mouseenter="highlightColumn(1)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.mood.badMood,
                  unchecked: !student.emotionalState.mood.badMood,
                }"
                @click="toggleCheckbox(student, 'mood.badMood')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 2 }"
              @mouseenter="highlightColumn(2)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.mood.anxiousMood,
                  unchecked: !student.emotionalState.mood.anxiousMood,
                }"
                @click="toggleCheckbox(student, 'mood.anxiousMood')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 3 }"
              @mouseenter="highlightColumn(3)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.mood.irritable,
                  unchecked: !student.emotionalState.mood.irritable,
                }"
                @click="toggleCheckbox(student, 'mood.irritable')"
              ></div>
            </td>

            <!-- Социальное взаимодействие -->
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 4 }"
              @mouseenter="highlightColumn(4)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.social.withdrawn,
                  unchecked: !student.emotionalState.social.withdrawn,
                }"
                @click="toggleCheckbox(student, 'social.withdrawn')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 5 }"
              @mouseenter="highlightColumn(5)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.social.conflicts,
                  unchecked: !student.emotionalState.social.conflicts,
                }"
                @click="toggleCheckbox(student, 'social.conflicts')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 6 }"
              @mouseenter="highlightColumn(6)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.social.overconfident,
                  unchecked: !student.emotionalState.social.overconfident,
                }"
                @click="toggleCheckbox(student, 'social.overconfident')"
              ></div>
            </td>

            <!-- Поведение -->
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 7 }"
              @mouseenter="highlightColumn(7)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.behavior.activityRefusal,
                  unchecked: !student.emotionalState.behavior.activityRefusal,
                }"
                @click="toggleCheckbox(student, 'behavior.activityRefusal')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 8 }"
              @mouseenter="highlightColumn(8)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.behavior.selfHarm,
                  unchecked: !student.emotionalState.behavior.selfHarm,
                }"
                @click="toggleCheckbox(student, 'behavior.selfHarm')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 9 }"
              @mouseenter="highlightColumn(9)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.behavior.untidyAppearance,
                  unchecked: !student.emotionalState.behavior.untidyAppearance,
                }"
                @click="toggleCheckbox(student, 'behavior.untidyAppearance')"
              ></div>
            </td>

            <!-- Мысли и чувства -->
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 10 }"
              @mouseenter="highlightColumn(10)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.thoughts.negativeStatements,
                  unchecked:
                    !student.emotionalState.thoughts.negativeStatements,
                }"
                @click="toggleCheckbox(student, 'thoughts.negativeStatements')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 11 }"
              @mouseenter="highlightColumn(11)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.thoughts.physicalComplaints,
                  unchecked:
                    !student.emotionalState.thoughts.physicalComplaints,
                }"
                @click="toggleCheckbox(student, 'thoughts.physicalComplaints')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 12 }"
              @mouseenter="highlightColumn(12)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.thoughts.deathTalks,
                  unchecked: !student.emotionalState.thoughts.deathTalks,
                }"
                @click="toggleCheckbox(student, 'thoughts.deathTalks')"
              ></div>
            </td>

            <!-- Внешние обстоятельства -->
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 13 }"
              @mouseenter="highlightColumn(13)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.circumstances.rejectedByClass,
                  unchecked:
                    !student.emotionalState.circumstances.rejectedByClass,
                }"
                @click="
                  toggleCheckbox(student, 'circumstances.rejectedByClass')
                "
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 14 }"
              @mouseenter="highlightColumn(14)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.circumstances.bullyingTarget,
                  unchecked:
                    !student.emotionalState.circumstances.bullyingTarget,
                }"
                @click="toggleCheckbox(student, 'circumstances.bullyingTarget')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 15 }"
              @mouseenter="highlightColumn(15)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.circumstances.unstableFamily,
                  unchecked:
                    !student.emotionalState.circumstances.unstableFamily,
                }"
                @click="toggleCheckbox(student, 'circumstances.unstableFamily')"
              ></div>
            </td>
            <td
              class="checkbox-cell"
              :class="{ highlighted: highlightedColumn === 16 }"
              @mouseenter="highlightColumn(16)"
              @mouseleave="resetHighlight"
            >
              <div
                class="status-icon"
                :class="{
                  checked: student.emotionalState.circumstances.significantLoss,
                  unchecked:
                    !student.emotionalState.circumstances.significantLoss,
                }"
                @click="
                  toggleCheckbox(student, 'circumstances.significantLoss')
                "
              ></div>
            </td>
            <td>
              <div v-if="student.emotionalState.lastUpdate">
                {{
                  student.emotionalState.lastUpdate == ""
                    ? ""
                    : new Date(
                        student.emotionalState.lastUpdate,
                      ).toLocaleDateString()
                }}
              </div>
            </td>

            <td style="padding: 0; margin: 0; height: 100%">
              <!-- @keydown.enter.prevent="editNotes(student)" -->
              <textarea
                v-model="student.emotionalState.notes"
                @change="editNotes(student)"
                style="
                  width: 100%;
                  height: 100%;
                  min-height: 60px;
                  padding: 4px 8px;
                  margin: 0;
                  font-size: 12px;
                  line-height: 1.4;
                  border: none;
                  border-radius: 0;
                  resize: vertical;
                  background: #f9f9f9;
                  font-family: inherit;
                  display: block;
                  box-sizing: border-box;
                "
                @focus="(e) => (e.target.style.background = '#fff')"
                @blur="(e) => (e.target.style.background = '#f9f9f9')"
              ></textarea>
            </td>
            <!-- Рейтинг риска -->
            <td
              class="score-cell"
              :class="getRiskClass(calculateRiskScore(student))"
            >
              {{ calculateRiskScore(student) }}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="no-data">
      {{
        riskFilter > 0
          ? "Нет студентов с риском выше " + riskFilter + "%"
          : "Нет данных для отображения"
      }}
    </div>
    <v-dialog v-model="loading" width="auto">
      <v-card
        height="70px"
        width="100px"
        style="display: flex; flex-direction: column; justify-content: center"
      >
        <Preloader v-if="loading" ref="Preloader"
      /></v-card>
    </v-dialog>
  </div>
</template>
<script>
import TutorialDataService from "../services/TutorialDataService";
const Preloader = () => import("./Preloader.vue");
export default {
  name: "EmotionalStateChecklist",
  components: {
    Preloader: Preloader,
  },
  props: {
    selectedClass: Object,
  },
  data() {
    return {
      studentsList: [],
      classList: [],
      selectedClassSelect: null,
      userRole: "admin",
      highlightedRow: null,
      highlightedColumn: null,
      riskFilter: 0, // Должно быть здесь
      loading: false,
    };
  },
  methods: {
    printTable() {
      const printWindow = window.open("", "_blank");
      const currentDate = new Date().toLocaleDateString("ru-RU");

      const tableHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Чек-лист психоэмоционального состояния</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .print-header { text-align: center; margin-bottom: 20px; }
        .print-header h2 { margin: 0 0 10px 0; }
        .print-date { text-align: center; margin-top: 20px; font-style: italic; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #000; }
        th, td { border: 1px solid #000; padding: 6px 4px; text-align: center; }
        th {  font-weight: bold; }
        .student-info { text-align: left; min-width: 120px; border: 1px solid #000; }
        .vertical-text {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          height: 120px;
          border: 1px solid #000 !important;
        }
        .vertical-content { display: inline-block; }
        .score-cell { font-weight: bold; border: 1px solid #000; }
        .risk-high { background: #ffebee; }
        .risk-medium { background: #fff3e0; }
        .risk-low { background: #e8f5e8; }
        .section-header { border: 1px solid #000; }
        .main-headers th { border: 1px solid #000; }
        .vertical-headers th { border: 1px solid #000; }
        @media print {
          body { margin: 15px; }
          table { border: 1px solid #000 !important; }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <h2>Чек-лист психоэмоционального состояния</h2>
      </div>

      <table>
        <thead>
          <tr class="main-headers">
            <th rowspan="2" class="student-col" style="border: 1px solid #000;">Ученик</th>
            <th colspan="3" class="section-header" style="border: 1px solid #000;">Эмоциональное состояние</th>
            <th colspan="3" class="section-header" style="border: 1px solid #000;">Социальное взаимодействие</th>
            <th colspan="3" class="section-header" style="border: 1px solid #000;">Поведение</th>
            <th colspan="3" class="section-header" style="border: 1px solid #000;">Мысли и чувства</th>
            <th colspan="4" class="section-header" style="border: 1px solid #000;">Внешние обстоятельства</th>
              <th rowspan="2" class="score-col" style="border: 1px solid #000;">Изменение</th>
               <th rowspan="2" class="score-col" style="border: 1px solid #000;">Комментарий</th>
            <th rowspan="2" class="score-col" style="border: 1px solid #000;">Риск</th>
          </tr>
          <tr class="vertical-headers">
            <th class="vertical-text" style="border: 1px solid #000;">Часто плохое настроение</th>
            <th class="vertical-text" style="border: 1px solid #000;">Часто тревожное, подавленное настроение</th>
            <th class="vertical-text" style="border: 1px solid #000;">Раздражителен, агрессивен</th>
            <th class="vertical-text" style="border: 1px solid #000;">Замкнут, стремится к уединению</th>
            <th class="vertical-text" style="border: 1px solid #000;">Ссоры с одноклассниками и педагогами</th>
            <th class="vertical-text" style="border: 1px solid #000;">Излишне самоуверен/чувство неполноценности</th>
            <th class="vertical-text" style="border: 1px solid #000;">Отказ от привычных видов деятельности</th>
            <th class="vertical-text" style="border: 1px solid #000;">Самоповреждения</th>
            <th class="vertical-text" style="border: 1px solid #000;">Неопрятный внешний вид</th>
            <th class="vertical-text" style="border: 1px solid #000;">Негативные высказывания о себе</th>
            <th class="vertical-text" style="border: 1px solid #000;">Жалобы на физические боли</th>
            <th class="vertical-text" style="border: 1px solid #000;">Разговоры о смерти</th>
            <th class="vertical-text" style="border: 1px solid #000;">Отвержение коллективом</th>
            <th class="vertical-text" style="border: 1px solid #000;">Объект травли</th>
            <th class="vertical-text" style="border: 1px solid #000;">Неустойчивая семейная ситуация</th>
            <th class="vertical-text" style="border: 1px solid #000;">Значимая потеря</th>
          </tr>
        </thead>
        <tbody>
          ${this.filteredStudentsList
            .map(
              (student) => `
            <tr>
              <td class="student-info" style="border: 1px solid #000;">
                <strong>${this.getStudentClass(student)} ${student.FirstName} ${
                student.LastName
              }</strong>
              </td>
              <!-- Эмоциональное состояние -->
              <td style="border: 1px solid #000;">${
                student.emotionalState.mood.badMood
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.mood.anxiousMood
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.mood.irritable
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <!-- Социальное взаимодействие -->
              <td style="border: 1px solid #000;">${
                student.emotionalState.social.withdrawn
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.social.conflicts
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.social.overconfident
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <!-- Поведение -->
              <td style="border: 1px solid #000;">${
                student.emotionalState.behavior.activityRefusal
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.behavior.selfHarm
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.behavior.untidyAppearance
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <!-- Мысли и чувства -->
              <td style="border: 1px solid #000;">${
                student.emotionalState.thoughts.negativeStatements
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.thoughts.physicalComplaints
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.thoughts.deathTalks
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <!-- Внешние обстоятельства -->
              <td style="border: 1px solid #000;">${
                student.emotionalState.circumstances.rejectedByClass
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.circumstances.bullyingTarget
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.circumstances.unstableFamily
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
              <td style="border: 1px solid #000;">${
                student.emotionalState.circumstances.significantLoss
                  ? '<span class="checkmark">✓</span>'
                  : ""
              }</td>
               <td>
              <div v-if="student.emotionalState.lastUpdate">
               ${
                 student.emotionalState.lastUpdate == ""
                   ? ""
                   : new Date(
                       student.emotionalState.lastUpdate,
                     ).toLocaleDateString()
               }
              </div>
            </td>
            <td>
              <div v-if="student.emotionalState.notes">
                      ${student.emotionalState.notes}
              </div>
            </td>
              <!-- Рейтинг риска -->
              <td class="score-cell ${this.getRiskClass(
                this.calculateRiskScore(student),
              )}" style="border: 1px solid #000;">
                ${this.calculateRiskScore(student)}%
              </td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>

      <div class="print-date">
        По состоянию на ${currentDate}
      </div>
    </body>
    </html>
  `;

      printWindow.document.write(tableHtml);
      // printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        // printWindow.close();
      }, 250);
    },
    applyRiskFilter() {
      this.$forceUpdate();
    },

    // Сбросить фильтр
    clearRiskFilter() {
      this.riskFilter = 0;
      this.loadStudents();
    },
    getCheckboxPath(section, index) {
      const paths = {
        mood: ["badMood", "anxiousMood", "irritable"],
        social: ["withdrawn", "conflicts", "overconfident"],
        behavior: ["activityRefusal", "selfHarm", "untidyAppearance"],
        thoughts: ["negativeStatements", "physicalComplaints", "deathTalks"],
        circumstances: [
          "rejectedByClass",
          "bullyingTarget",
          "unstableFamily",
          "significantLoss",
        ],
      };
      return `${section}.${paths[section][index - 1]}`;
    },

    getCheckboxClass(student, section, index) {
      const path = this.getCheckboxPath(section, index);
      const paths = path.split(".");
      let current = student.emotionalState;

      for (let i = 0; i < paths.length; i++) {
        current = current[paths[i]];
      }

      return {
        checked: current,
        unchecked: !current,
      };
    },
    toggleCheckbox(student, path) {
      const paths = path.split(".");
      let current = student.emotionalState;

      for (let i = 0; i < paths.length - 1; i++) {
        current = current[paths[i]];
      }

      current[paths[paths.length - 1]] = !current[paths[paths.length - 1]];
      this.updateStudentState(student);
    },
    editNotes(student) {
      this.updateStudentState(student);
    },
    async loadStudents() {
      try {
        if (this.selectedClassSelect == "" && this.riskFilter == 0)
          this.riskFilter = 1;
        let params = {};
        if (this.selectedClass.classID != "admin") {
          document.getElementById("selected_class").disabled = true;
          params.classID = this.selectedClass.classID;
        } else if (this.selectedClassSelect && this.selectedClassSelect._id) {
          params.classID = this.selectedClassSelect._id;
        }
        this.loading = true;

        const response = await TutorialDataService.findStudentByClassID(params);

        const studentsObject = response.data || response;
        const studentsArray = Object.values(studentsObject);

        this.studentsList = studentsArray.map((student) => {
          if (!student.emotionalState) {
            student.emotionalState = this.getDefaultEmotionalState();
          }
          this.loading = false;
          return student;
        });
      } catch (error) {
        console.error("Ошибка загрузки студентов:", error);
        this.studentsList = [];
        this.loading = false;
      }
    },

    async loadClassList() {
      try {
        const response = await TutorialDataService.getAllCLass();
        this.classList = response.data;
      } catch (error) {
        console.error("Ошибка загрузки классов:", error);
      }
    },

    async updateStudentState(student) {
      try {
        student.emotionalState.lastUpdate = new Date();
        this.loading = true;
        await TutorialDataService.updateStudentEmotionalState(student._id, {
          emotionalState: student.emotionalState,
        }).then((data) => {
          console.log(data);
          this.loading = false;
        });
      } catch (error) {
        console.error("Ошибка сохранения:", error);
        alert("Ошибка сохранения!");
      }
    },

    getStudentClass(student) {
      const classInfo = this.classList.find(
        (cls) => cls._id === student.classID,
      );
      return classInfo ? classInfo.className : "Не указан";
    },

    getDefaultEmotionalState() {
      return {
        mood: { badMood: false, anxiousMood: false, irritable: false },
        social: { withdrawn: false, conflicts: false, overconfident: false },
        behavior: {
          activityRefusal: false,
          selfHarm: false,
          untidyAppearance: false,
        },
        thoughts: {
          negativeStatements: false,
          physicalComplaints: false,
          deathTalks: false,
        },
        circumstances: {
          rejectedByClass: false,
          bullyingTarget: false,
          unstableFamily: false,
          significantLoss: false,
        },
        lastUpdate: "",
        notes: "",
      };
    },

    // Расчет рейтинга риска
    calculateRiskScore(student) {
      if (!student.emotionalState) return 0;

      const state = student.emotionalState;
      let score = 0;
      let total = 0;

      // Веса для разных категорий
      const weights = {
        mood: 1,
        social: 1,
        behavior: 2, // Поведение важнее
        thoughts: 3, // Мысли самые важные
        circumstances: 1.5, // Обстоятельства средние
      };

      // Подсчет баллов
      Object.values(state.mood).forEach((val) => {
        if (val) score += weights.mood;
        total += weights.mood;
      });
      Object.values(state.social).forEach((val) => {
        if (val) score += weights.social;
        total += weights.social;
      });
      Object.values(state.behavior).forEach((val) => {
        if (val) score += weights.behavior;
        total += weights.behavior;
      });
      Object.values(state.thoughts).forEach((val) => {
        if (val) score += weights.thoughts;
        total += weights.thoughts;
      });
      Object.values(state.circumstances).forEach((val) => {
        if (val) score += weights.circumstances;
        total += weights.circumstances;
      });

      return total > 0 ? Math.round((score / total) * 100) : 0;
    },

    getRiskClass(score) {
      if (score >= 70) return "risk-high";
      if (score >= 40) return "risk-medium";
      return "risk-low";
    },

    // Подсветка столбцов
    highlightColumn(columnIndex) {
      this.highlightedColumn = columnIndex;
    },

    resetHighlight() {
      this.highlightedColumn = null;
    },
  },
  async mounted() {
    await this.loadClassList();

    if (this.selectedClass.classID != "admin") {
      document.getElementById("selected_class").disabled = true;

      this.loadStudents();
    }
  },

  computed: {
    columnClasses() {
      const classes = {};
      if (this.highlightedColumn !== null) {
        classes[`col-${this.highlightedColumn}`] = true;
      }
      return classes;
    },

    filteredStudentsList() {
      let filtered = this.studentsList;

      // Фильтрация по риску
      if (this.riskFilter > 0) {
        filtered = filtered.filter(
          (student) => this.calculateRiskScore(student) >= this.riskFilter,
        );
      }

      // Сортировка по классам и имени
      return filtered.sort((a, b) => {
        // Сначала сравниваем классы
        const classA = this.getStudentClass(a);
        const classB = this.getStudentClass(b);

        if (classA < classB) return -1;
        if (classA > classB) return 1;

        // Если классы одинаковые, сортируем по имени
        const firstNameA = a.FirstName || "";
        const firstNameB = b.FirstName || "";

        return firstNameA.localeCompare(firstNameB);
      });
    },
  },
};
</script>

<style scoped>
.emotional-state-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.class-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 150px;
}

.students-table {
  overflow: auto;
  background: white;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* Основные стили таблицы */
table {
  min-width: 900px;
  border-collapse: collapse;
  font-size: 13px;
}

/* Контейнер для sticky заголовков */
thead {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
}

th,
td {
  border: 1px solid #e0e0e0;
  padding: 8px 6px;
  position: relative;
}

/* Сохраняем подсветку строк */
.student-row:hover td:not(.student-info) {
  background: #b8deff !important;
}

/* Подсветка строк */
.student-row:hover {
  background: #b8deff !important;
}

/* Заголовки */
.main-headers th {
  background: #f5f5f5;
  font-weight: 600;
  font-size: 12px;
  position: sticky;
  top: 0;
  z-index: 101;
}

.vertical-headers th {
  background: #f8f9fa;
  font-weight: 500;
  height: 120px;
  vertical-align: top;
  padding: 5px 2px;
  position: sticky;
  top: 38px; /* Высота первой строки заголовков */
  z-index: 101;
}

.vertical-text {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-orientation: mixed;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.3;
  padding: 8px 4px;
  font-size: 11px;
  cursor: help;
}

.vertical-content {
  display: inline-block;
  overflow: hidden;
}

.student-col {
  background: #e3f2fd;
  min-width: 120px;
  max-width: 140px;
  position: sticky;
  left: 0;
  z-index: 99;
}

.score-col {
  background: #f5f5f5;
  min-width: 60px;
  font-weight: 600;
}

.student-info {
  text-align: left;
  font-size: 15px;
  line-height: 1.3;
}

.checkbox-cell {
  min-width: 40px;
  cursor: pointer;
}

.score-cell {
  font-weight: bold;
  font-size: 13px;
}

.risk-low {
  background: #e8f5e8;
  color: #2e7d32;
}
.risk-medium {
  background: #fff3e0;
  color: #ef6c00;
}
.risk-high {
  background: #ffebee;
  color: #c62828;
}

/* Стили для статусных иконок */
.status-icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  border-radius: 30%;
  justify-content: center;
  align-items: center;
  transition: all 0.2s;
  cursor: pointer;
  margin: 0 auto;
}

.status-icon.unchecked {
  background-color: #dfdfdf;
}

.status-icon.unchecked::after {
  content: "✕";
  color: white;
  font-weight: bold;
  font-size: 16px;
  text-shadow: 0 0 1px #999;
}

.status-icon.checked {
  background-color: #ffc5ce;
}

.status-icon.checked::after {
  content: "✓";
  color: #c62828;
  font-weight: bold;
  font-size: 16px;
  border: #c62828 1px;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}

/* Фикс для sticky в Firefox */
@supports (-moz-appearance: none) {
  thead {
    background: white;
  }
}

/* Подсветка колонок */
/* Исправленная подсветка колонок */
td {
  position: relative;
  overflow: hidden;
}

td:hover:after {
  pointer-events: none;
  position: initial;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100%;
  height: 1000px;
  content: "";
  background-color: rgba(106, 241, 246, 0.678);
}

/* Отключаем подсветку для ячеек с учениками */
.student-info:hover::before {
  display: none;
}

/* Подсветка столбцов */
.checkbox-cell.highlighted,
.score-cell.highlighted {
  background-color: rgba(106, 241, 246, 0.4) !important;
}

/* Убедитесь, что эти стили переопределяют существующие */
.student-row td.checkbox-cell.highlighted,
.student-row td.score-cell.highlighted {
  background-color: rgba(106, 241, 246, 0.4) !important;
}

/* Убираем старый способ подсветки через псевдоэлементы */
td:hover:after {
  display: none;
}

/* Добавляем подсветку через классы на основе номера столбца */
:root {
  --highlighted-column: 0;
}

/* Динамическая подсветка столбцов */
.checkbox-cell:nth-child(1).highlighted,
.score-cell:nth-child(18).highlighted {
  /* 18 потому что первый столбец ученик + 17 столбцов */
  background-color: rgba(106, 241, 246, 0.4) !important;
}

.risk-filter {
  margin: 15px 0;
  padding: 12px 15px;
  background: #f8f9fa;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 200px 1fr 150px 80px; /* Фиксированные колонки */
  gap: 10px;
  align-items: center;
  max-width: 500px;
}

.risk-filter label {
  font-weight: 500;
  white-space: nowrap;
}

.risk-value {
  font-weight: bold;
  color: #1976d2;
  text-align: center;
  font-family: "Courier New", monospace;
}

.clear-filter-btn {
  background: #b4b4b4;
  color: white;
  border: none;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
}
.clear-filter-btn:hover {
  background: #a0a0a0;
}

.print-controls {
  margin: 15px 0;
  text-align: right;
}

.print-btn {
  background: #1976d2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.print-btn:hover {
  background: #1565c0;
}

/* Скрываем элементы при печати на основной странице */
@media print {
  .risk-filter,
  .print-controls,
  .page-header,
  .class-selector {
    display: none !important;
  }
}
</style>
